import cssMobile from '../../css/editorMobile.module.css'
import {useEffect, useState} from 'react'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {
  faAlignLeft,
  faArrowsAlt,
  faArrowsAltH,
  faArrowsAltV,
  faFileImage,
  faPlus,
  faSortNumericUp,
  faStar,
  faSun,
  faTrash,
  faUndo,
} from '@fortawesome/free-solid-svg-icons'
import {useAppDispatch, useAppSelector} from '../../stores/configureStore'
import {
  addImageHistoryItem,
  deleteImage,
} from '../../stores/editor/editor.reducer'
import {Img} from '../../models/editor/img'
import {IMG_REMOVED} from '../../models/editor/imgHistoryItemAction'
import MaskedPhotoTextControls from './controls/MaskedPhotoTextControls'
import ReorderControls from './controls/ReorderControls'
import FilterControls from './controls/FilterControls'
import BrightnessControls from './controls/BrightnessControls'
import SizeControlsPhoto from './controls/SizeControlsPhoto'
import RotationControls from './controls/RotationControls'
import MoveControls from './controls/MoveControls'
import MirrorControls from './controls/MirrorControls'
import MoveInsideControls from './controls/MoveInsideControls'
import {selectToolboxTabPanel} from '../../stores/toolbox/toolbox.reducer'
import {useTranslation} from 'react-i18next'

interface MaskedPhotoProps {
  active: boolean
  setControlOpen: React.Dispatch<React.SetStateAction<boolean>>
  openModal: () => void
}

export default function MaskedPhoto({
  active,
  setControlOpen,
  openModal,
}: MaskedPhotoProps) {
  const {t} = useTranslation()
  const [currentlyEditing, setCurrentlyEditing] = useState<
    | 'text'
    | 'addimage'
    | 'reorder'
    | 'filter'
    | 'brightness'
    | 'size'
    | 'rotation'
    | 'move'
    | 'mirror'
    | 'inside'
    | undefined
  >(undefined)
  const [insideIndex, setInsideIndex] = useState<number | undefined>(undefined)
  const dispatch = useAppDispatch()
  const {images, selectedImageId} = useAppSelector(store => store.editor)
  const selectedImage = images.find(
    (image: Img) => image.id === selectedImageId,
  )
  const closeControl = () => {
    setCurrentlyEditing(undefined)
  }

  useEffect(() => {
    setControlOpen(currentlyEditing !== undefined)
    if (currentlyEditing !== 'inside') {
      setInsideIndex(undefined)
    }
  }, [setControlOpen, currentlyEditing])

  useEffect(() => {
    closeControl()
    setInsideIndex(undefined)
  }, [active])

  return (
    <>
      <div className={`${cssMobile.tab} ${active ? cssMobile.active : ''}`}>
        <div className={cssMobile.action} onClick={openModal}>
          <FontAwesomeIcon icon={faPlus} />
          <span>{t('Hozzáadás')}</span>
        </div>
        <div className={cssMobile.scrollContainer}>
          <div
            className={cssMobile.action}
            onClick={() => {
              if (selectedImage) {
                dispatch(deleteImage({id: selectedImage.id}))
                dispatch(selectToolboxTabPanel({tabPanel: 'Graphics'}))
                dispatch(
                  addImageHistoryItem({
                    item: {img: selectedImage, action: IMG_REMOVED},
                  }),
                )
              }
            }}>
            <FontAwesomeIcon icon={faTrash} />
            <span>{t('Törlés')}</span>
          </div>
          <div
            className={cssMobile.action}
            onClick={() => {
              setCurrentlyEditing('text')
            }}>
            <FontAwesomeIcon icon={faAlignLeft} />
            <span>{t('Szöveg')}</span>
          </div>
          {selectedImage?.type === 'masked'
            ? selectedImage.inside.map((_, index) => (
                <div
                  className={cssMobile.action}
                  onClick={() => {
                    dispatch(
                      selectToolboxTabPanel({
                        tabPanel: 'PhotoUpload',
                        selectForMasked: {
                          id: selectedImage.id,
                          insideIndex: index,
                        },
                      }),
                    )
                  }}>
                  <FontAwesomeIcon icon={faFileImage} />
                  <span>
                    {selectedImage.inside.length === 1
                      ? t('Kis kép')
                      : t('{{i}}. kép', {i: index + 1})}
                  </span>
                </div>
              ))
            : null}

          <div
            className={cssMobile.action}
            onClick={() => {
              setCurrentlyEditing('reorder')
            }}>
            <FontAwesomeIcon icon={faSortNumericUp} />
            <span>{t('Sorrend')}</span>
          </div>
          <div
            className={cssMobile.action}
            onClick={() => {
              setCurrentlyEditing('filter')
            }}>
            <FontAwesomeIcon icon={faStar} />
            <span>{t('Effektek')}</span>
          </div>
          <div
            className={cssMobile.action}
            onClick={() => {
              setCurrentlyEditing('brightness')
            }}>
            <FontAwesomeIcon icon={faSun} />
            <span>{t('Fényerő')}</span>
          </div>
          <div
            className={cssMobile.action}
            onClick={() => {
              setCurrentlyEditing('size')
            }}>
            <FontAwesomeIcon icon={faArrowsAltV} />
            <span>{t('Méret')}</span>
          </div>
          <div
            className={cssMobile.action}
            onClick={() => {
              setCurrentlyEditing('rotation')
            }}>
            <FontAwesomeIcon icon={faUndo} />
            <span>{t('Forgatás')}</span>
          </div>
          <div
            className={cssMobile.action}
            onClick={() => {
              setCurrentlyEditing('move')
            }}>
            <FontAwesomeIcon icon={faArrowsAlt} />
            <span>{t('Grafika')}</span>
          </div>
          <div
            className={cssMobile.action}
            onClick={() => {
              setCurrentlyEditing('mirror')
            }}>
            <FontAwesomeIcon icon={faArrowsAltH} />
            <span>{t('Tükrözés')}</span>
          </div>
          {selectedImage?.type === 'masked'
            ? selectedImage.inside.map((_, index) => (
                <div
                  className={cssMobile.action}
                  onClick={() => {
                    setCurrentlyEditing('inside')
                    setInsideIndex(index)
                  }}>
                  <FontAwesomeIcon icon={faArrowsAlt} />
                  <span>
                    {selectedImage.inside.length === 1
                      ? t('Kis kép')
                      : t('{{i}}. kép', {i: index + 1})}
                  </span>
                </div>
              ))
            : null}
        </div>
      </div>
      <MaskedPhotoTextControls
        open={currentlyEditing === 'text'}
        onBackClick={closeControl}
      />
      <ReorderControls
        open={currentlyEditing === 'reorder'}
        onBackClick={closeControl}
      />
      <FilterControls
        open={currentlyEditing === 'filter'}
        onBackClick={closeControl}
      />
      <BrightnessControls
        open={currentlyEditing === 'brightness'}
        onBackClick={closeControl}
      />
      <SizeControlsPhoto
        open={currentlyEditing === 'size'}
        onBackClick={closeControl}
      />
      <RotationControls
        open={currentlyEditing === 'rotation'}
        onBackClick={closeControl}
      />
      <MoveControls
        open={currentlyEditing === 'move'}
        onBackClick={closeControl}
      />
      <MirrorControls
        open={currentlyEditing === 'mirror'}
        onBackClick={closeControl}
      />
      <MoveInsideControls
        open={currentlyEditing === 'inside'}
        onBackClick={closeControl}
        index={insideIndex}
      />
    </>
  )
}
